import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { filter, first, map } from 'rxjs/operators';
import { RootState } from '@root/app.state';
import { ProfilePictureUploadDialogComponent } from '@platform/components/file-upload-widget/file-upload/profile-picture-upload-dialog/profile-picture-upload-dialog.component';
import { DialogService } from '@platform/dialog/dialog.service';
import { selectDialog } from '@platform/platform.selectors';
import { getFileExtension } from '@platform/shared/utilities/file-utility';

import * as PlatformActions from '@platform/platform.actions';

import { ToastService } from '@platform/toast/toast.service';
import { firstValueFrom } from 'rxjs';
import { CommonModule, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { LinkButtonComponent } from '@platform/components/buttons/link-button/link-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { DialogBaseComponent } from '@platform/components/dialog-base/dialog-base.component';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-profile-picture-dialog',
  templateUrl: './profile-picture-dialog.component.html',
  styleUrls: ['./profile-picture-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, RouterModule, TranslateModule, DialogBaseComponent, LinkButtonComponent],
})
export class ProfilePictureDialogComponent {
  thumbnailPhoto;
  thumbnailPhotoFake: any;
  file!: File;
  readonly allowedFileFormats = ['bmp', 'jpg', 'jpeg', 'png'];

  constructor(
    private dialogRef: MatDialogRef<ProfilePictureDialogComponent>,
    private store$: Store<RootState>,
    private dialogService: DialogService,
    private toastService: ToastService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.thumbnailPhoto = data.thumbnailPhoto;
  }

  @HostListener('document:keydown.enter', ['$event'])
  async enter($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();

    const dialogs = await firstValueFrom(
      this.store$.pipe(
        select(selectDialog),
        map((state) => state.dialogs),
        first(), // an closed observable is required to make the promise returning
      ),
    );
    if (dialogs.some((d) => d.id !== this.dialogRef.id && d.isOpen)) {
      return;
    }
    this.saveDialog();
  }

  editPicture() {
    this.dialogService
      .openDialog(ProfilePictureUploadDialogComponent, {
        width: '800px',
        autoFocus: false,
        disableClose: true,
        data: {
          multiple: true,
        },
      })
      .pipe(filter((data) => !!data))
      .subscribe((data: any) => {
        const file = data[0];
        if (!this.areFileFormatsAllowed(file)) {
          this.toastService.showErrorToast('users.user-profile.errors.type');
          return;
        }
        if (file.size > 4194304) {
          this.toastService.showErrorToast('users.user-profile.errors.size');
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (_event) => {
          this.thumbnailPhotoFake = reader.result;
          const img = document.getElementById('fake') as HTMLImageElement;
          setTimeout(() => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;
            if (height !== width) {
              this.toastService.showErrorToast('users.user-profile.errors.ratio');
              return;
            }
            this.thumbnailPhoto = reader.result;
            this.file = file;
          }, 10);
        };
      });
  }

  closeDialog(): void {
    this.store$.dispatch(PlatformActions.closeDialog({ id: this.dialogRef.id }));
  }

  saveDialog() {
    let data;
    if (this.file !== undefined) {
      data = {
        file: this.file,
      };
    }
    this.store$.dispatch(
      PlatformActions.closeDialog({
        id: this.dialogRef.id,
        data,
      }),
    );
  }

  deletePicture() {
    this.thumbnailPhoto = null;
    this.file = null!;
  }

  areFileFormatsAllowed(file: File): boolean {
    const extension = getFileExtension(file.name);
    return this.allowedFileFormats.some((x) => x === extension);
  }
}
